import { apiFetch } from './client'
import { ApiError } from './errors'
import type { LoginTicket, SessionUser } from './auth'

/**
 * Tela 2 - PRIMEIRO ACESSO (S32; ADR-0030 D11 passos 2-4; ADR-0031 M-5).
 * Contrato: `apps/api/routes/auth.py` (`POST /api/auth/password/change` no modo
 * TICKET, `/mfa/enroll`, `/mfa/confirm`), DTOs em
 * `core_platform/identity_access/application/dtos.py`.
 *
 * SEGREDOS QUE PASSAM POR AQUI (D13 do ADR-0030): a senha provisoria e a nova, a
 * URI `otpauth://` (carrega o segredo TOTP) e os 10 codigos de recuperacao. Este
 * modulo NAO guarda nada: devolve ao chamador e esquece. A tela os mantem so em
 * memoria, com `gcTime: 0` no TanStack (S32-4), e os testes P4/P5 travam isso.
 */

interface PasswordChangeResponse {
  password_changed_at: string
  ticket: string | null
  ticket_type: string | null
  expires_in: number | null
  next_step: string | null
}

interface MfaEnrollResponse {
  otpauth_uri: string
  issuer: string
  account_name: string
  next_step: string
}

interface RecoveryCodesResponse {
  recovery_codes: string[]
  enrolled_at: string
  next_step: string
}

/** s33 (ADR-0031 M-5 passo 3). Tokens deliberadamente AUSENTES do tipo (S22-5, A9). */
interface EmailConfirmResponse {
  recovery_codes: string[]
  email_verified_at: string
  session: { user: { id: string; tenant_id: string; username: string; email: string } }
}

export interface MfaEnrollment {
  otpauthUri: string
  accountName: string
}

/**
 * Modo TICKET (primeiro acesso). A resposta traz um ticket NOVO (F-8): mesmo
 * usuario, `jti` diferente, relogio de 300 s recomecado. E ELE que segue para
 * enroll/confirm/login-mfa (P55 do runbook B5.4).
 */
export async function changePasswordFirstAccess(input: {
  ticket: string
  currentPassword: string
  newPassword: string
}): Promise<LoginTicket> {
  const r = await apiFetch<PasswordChangeResponse>('/api/auth/password/change', {
    method: 'POST',
    body: {
      current_password: input.currentPassword,
      new_password: input.newPassword,
      ticket: input.ticket,
    },
  })
  return { ticket: r.ticket ?? '', nextStep: r.next_step ?? '', expiresIn: r.expires_in ?? 0 }
}

export async function startMfaEnrollment(input: { ticket: string }): Promise<MfaEnrollment> {
  const r = await apiFetch<MfaEnrollResponse>('/api/auth/mfa/enroll', {
    method: 'POST',
    body: { ticket: input.ticket },
  })
  return { otpauthUri: r.otpauth_uri, accountName: r.account_name }
}

export async function confirmMfaEnrollment(input: {
  ticket: string
  code: string
}): Promise<{ recoveryCodes: string[] }> {
  const r = await apiFetch<RecoveryCodesResponse>('/api/auth/mfa/confirm', {
    method: 'POST',
    body: { ticket: input.ticket, code: input.code },
  })
  return { recoveryCodes: r.recovery_codes }
}

/**
 * s33 (ADR-0031 M-5 passo 3, S33-6): pede o envio do codigo de 8 digitos para o
 * e-mail CADASTRADO. Um envio por ticket (M-7): repetir responde igual, sem e-mail novo.
 */
export async function startEmailVerification(input: { ticket: string }): Promise<{ expiresIn: number }> {
  const r = await apiFetch<{ expires_in: number }>('/api/auth/mfa/email/start', {
    method: 'POST',
    body: { ticket: input.ticket },
  })
  return { expiresIn: r.expires_in }
}

/**
 * s33 (S33-5): codigo certo -> e-mail validado, 10 codigos UMA vez e a SESSAO ja
 * aberta pelo servidor (cookie HttpOnly). Devolve so os codigos e o usuario: os
 * tokens do corpo nao saem daqui (A9).
 */
export async function confirmEmailVerification(input: {
  ticket: string
  code: string
}): Promise<{ recoveryCodes: string[]; user: SessionUser }> {
  const r = await apiFetch<EmailConfirmResponse>('/api/auth/mfa/email/confirm', {
    method: 'POST',
    body: { ticket: input.ticket, code: input.code },
  })
  const u = r.session.user
  return {
    recoveryCodes: r.recovery_codes,
    user: { id: u.id, tenantId: u.tenant_id, username: u.username, email: u.email },
  }
}

/** Codigos estaveis do 422 da troca (exception_handlers.py; B5.2/B5.3b). */
export const PASSWORD_ERROR_CODES = [
  'too_short',
  'too_long',
  'equals_username',
  'equals_email',
  'equals_current',
  'current_password_mismatch',
] as const

export type PasswordErrorCode = (typeof PASSWORD_ERROR_CODES)[number] | 'unknown'

/**
 * 422 -> codigo estavel (ou 'unknown' se o servidor mandar um codigo que esta
 * versao da SPA nao conhece). Qualquer outro erro -> null (quem trata e o
 * chamador: 401 = tempo acabou; rede = conexao).
 */
export function passwordChangeErrorCode(error: unknown): PasswordErrorCode | null {
  if (!(error instanceof ApiError) || error.status !== 422) return null
  const detail = error.detail as { code?: unknown } | null
  const code = typeof detail?.code === 'string' ? detail.code : ''
  return (PASSWORD_ERROR_CODES as readonly string[]).includes(code)
    ? (code as PasswordErrorCode)
    : 'unknown'
}

/** A chave manual = parametro `secret` da URI otpauth:// (e so ele). */
export function secretFromOtpauth(uri: string): string | null {
  if (!uri.startsWith('otpauth://')) return null
  const q = uri.indexOf('?')
  if (q < 0) return null
  return new URLSearchParams(uri.slice(q + 1)).get('secret')
}

/** "JBSWY3DPEHPK3PXP" -> "JBSW Y3DP EHPK 3PXP" (mais facil de digitar). */
export function groupSecret(secret: string): string {
  return secret.replace(/(.{4})(?=.)/g, '$1 ')
}
